import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import VerifiedOrgBadge from "@/components/VerifiedOrgBadge";
import ShareButton from "@/components/ShareButton";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Briefcase, Building2, MapPin, ExternalLink, Globe, Check, DollarSign, ArrowLeft } from "lucide-react";
import { BRANCH_META } from "@/lib/branches";

const ROLE_TYPE_LABEL = {
    full_time: "Full-time",
    part_time: "Part-time",
    contract: "Contract",
    internship: "Internship",
};

export default function JobDetail() {
    const { id } = useParams();
    const [sp] = useSearchParams();
    const listed = sp.get("listed");
    const { user } = useAuth();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get(`/jobs/${id}`)
            .then((r) => setJob(r.data))
            .catch((e) => setError(e.response?.data?.detail || "Job not found"))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) return (
        <div className="min-h-screen w-full"><TopBar /><div className="max-w-3xl mx-auto pt-24 px-4 text-slate-500">Loading…</div></div>
    );
    if (error) return (
        <div className="min-h-screen w-full"><TopBar /><div className="max-w-3xl mx-auto pt-24 px-4 text-slate-500" data-testid="job-error">{error}</div></div>
    );

    return (
        <div className="min-h-screen w-full">
            <TopBar />
            <div className="max-w-3xl mx-auto pt-24 pb-16 px-4 sm:px-8">
                {listed === "1" && (
                    <div className="mb-4 rounded-2xl px-4 py-3 bg-emerald-50 text-emerald-800 flex items-center gap-2" data-testid="listing-success">
                        <Check className="w-4 h-4" /> Listing is live. Payment received.
                    </div>
                )}
                {listed === "0" && (
                    <div className="mb-4 rounded-2xl px-4 py-3 bg-amber-50 text-amber-800" data-testid="listing-canceled">
                        Payment was canceled — the listing is still in draft. Retry from the Careers page.
                    </div>
                )}

                <Link to="/careers" className="text-slate-500 hover:text-slate-900 text-sm inline-flex items-center gap-1" data-testid="back-careers">
                    <ArrowLeft className="w-3 h-3" /> Careers
                </Link>

                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="crystal rounded-3xl p-6 sm:p-8 mt-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="chip" style={{ background: BRANCH_META.business.soft, color: BRANCH_META.business.text }}>
                            <Briefcase className="w-3 h-3" /> {ROLE_TYPE_LABEL[job.role_type] || "Role"}
                        </span>
                        {job.location && (
                            <span className="chip bg-white/70 text-slate-700">
                                <MapPin className="w-3 h-3" /> {job.location}
                            </span>
                        )}
                        {job.compensation_note && (
                            <span className="chip bg-white/70 text-slate-700">
                                <DollarSign className="w-3 h-3" /> {job.compensation_note}
                            </span>
                        )}
                    </div>

                    <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-slate-900 mt-3 leading-none" data-testid="job-title-heading">
                        {job.title}
                    </h1>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-500" />
                        <span className="font-display font-bold text-slate-900" data-testid="job-org-name">
                            {job.org_name || job.posted_by_name}
                        </span>
                        {job.org_verified && <VerifiedOrgBadge />}
                        {job.org_website && (
                            <a
                                href={job.org_website}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm text-humanities font-bold inline-flex items-center gap-1"
                                data-testid="job-org-website"
                            >
                                <Globe className="w-3 h-3" /> {job.org_website.replace(/^https?:\/\//, "")}
                            </a>
                        )}
                        <div className="ml-auto">
                            <ShareButton path={`/jobs/${job.id}`} title={job.title} refCode={user?.referral_code} />
                        </div>
                    </div>

                    <div className="mt-6 prose prose-slate max-w-none whitespace-pre-wrap text-slate-700" data-testid="job-description">
                        {job.description}
                    </div>

                    <div className="mt-6">
                        <div className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-1.5">
                            Recruiting in
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {(job.discipline_slugs || []).map((slug) => (
                                <Link
                                    key={slug}
                                    to={`/discipline/${slug}`}
                                    className="chip bg-white/70 text-slate-700 hover:bg-slate-900 hover:text-cream transition-all"
                                    data-testid={`job-discipline-${slug}`}
                                >
                                    #{slug}
                                </Link>
                            ))}
                        </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-white/60 flex flex-wrap items-center justify-between gap-3">
                        <p className="text-xs text-slate-500">
                            Posted by <span className="font-display font-bold text-slate-700">{job.posted_by_name}</span> ·{" "}
                            {new Date(job.created_at).toLocaleDateString()}
                        </p>
                        {job.apply_url ? (
                            <a
                                href={job.apply_url}
                                target="_blank"
                                rel="noreferrer"
                                className="arcade-btn"
                                data-variant="primary"
                                data-testid="job-apply-btn"
                            >
                                <ExternalLink className="w-4 h-4" /> Apply on {new URL(job.apply_url, "https://x").hostname.replace("www.", "") || "site"}
                            </a>
                        ) : (
                            <span className="text-xs text-slate-500">No apply link provided.</span>
                        )}
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
